import { apiRequest } from "./client";
import {
  getDemoVideo,
  isDemoSessionId,
  isDemoVideoId,
  makeDemoSessionId,
} from "../data/demoVideos";

export type Session = {
  id: string;
  childId: string;
  videoId: string;
  startedAt: string;
  secondsWatched: number;
};

export type StopReason = "parent" | "daily" | "session" | null;

export type HeartbeatResponse = {
  remainingDailySeconds: number;
  sessionLimitSeconds: number;
  shouldStop: boolean;
  stopReason: StopReason;
};

export type StopResponse = {
  ok: boolean;
  delivered?: boolean;
  alreadyEnded?: boolean;
};

export type ActiveSession = {
  sessionId: string;
  childId: string;
  videoId: string;
  videoTitle: string;
  videoCategory: string | null;
  durationSec: number;
  secondsWatched: number;
  startedAt: string;
};

export const sessionsApi = {
  active: (token: string) =>
    apiRequest<{ sessions: ActiveSession[] }>("/v1/sessions/active", {
      token,
    }),

  start: (
    childId: string,
    videoId: string,
    token: string,
  ): Promise<{ session: Session; remainingSeconds: number }> => {
    if (isDemoVideoId(videoId)) {
      const session: Session = {
        id: makeDemoSessionId(videoId),
        childId,
        videoId,
        startedAt: new Date().toISOString(),
        secondsWatched: 0,
      };
      return Promise.resolve({ session, remainingSeconds: 60 * 60 });
    }
    return apiRequest<{ session: Session; remainingSeconds: number }>(
      "/v1/sessions",
      { method: "POST", body: { childId, videoId }, token },
    );
  },

  heartbeat: (
    sessionId: string,
    secondsWatched: number,
    token: string,
  ): Promise<HeartbeatResponse> => {
    if (isDemoSessionId(sessionId)) {
      return Promise.resolve({
        remainingDailySeconds: 60 * 60,
        sessionLimitSeconds: 60 * 60,
        shouldStop: false,
        stopReason: null,
      });
    }
    return apiRequest<HeartbeatResponse>(
      `/v1/sessions/${sessionId}/heartbeat`,
      { method: "POST", body: { secondsWatched }, token },
    );
  },

  end: (
    sessionId: string,
    secondsWatched: number,
    token: string,
  ): Promise<Session> => {
    if (isDemoSessionId(sessionId)) {
      return Promise.resolve({
        id: sessionId,
        childId: "",
        videoId: "",
        startedAt: new Date().toISOString(),
        secondsWatched,
      });
    }
    return apiRequest<Session>(`/v1/sessions/${sessionId}/end`, {
      method: "POST",
      body: { secondsWatched },
      token,
    });
  },

  // Parent → backend → TV: terminate the active session remotely.
  // The TV receives this via WebSocket (`parent_command: stop`) and
  // also via its next heartbeat as a failsafe.
  stop: (sessionId: string, token: string): Promise<StopResponse> => {
    if (isDemoSessionId(sessionId)) {
      return Promise.resolve({ ok: true, delivered: true });
    }
    return apiRequest<StopResponse>(`/v1/sessions/${sessionId}/stop`, {
      method: "POST",
      token,
    });
  },

  submitAnswer: (
    sessionId: string,
    questionId: string,
    selectedOption: number,
    token: string,
  ): Promise<AnswerResult> => {
    if (isDemoSessionId(sessionId)) {
      // Look up the demo video by its id embedded in the session id,
      // then resolve against its question list.
      const idMatch = sessionId.match(/^demo-session-(demo-[^-]+)-/);
      const videoId = idMatch?.[1] ?? null;
      const demo = videoId ? getDemoVideo(videoId) : undefined;
      const q = demo?.questions.find((qq) => qq.id === questionId);
      return Promise.resolve({
        correct: q ? q.correct === selectedOption : false,
        newStickers: [],
      });
    }
    return apiRequest<AnswerResult>(`/v1/sessions/${sessionId}/answer`, {
      method: "POST",
      body: { questionId, selectedOption },
      token,
    });
  },
};

// A sticker the child just unlocked by answering. The backend returns these
// on the answer response so the kid UI can celebrate immediately.
export type EarnedSticker = {
  key: string;
  title: string;
  emoji: string;
  description: string;
  group: "milestones" | "topics" | "streaks";
};

export type AnswerResult = {
  correct: boolean;
  // Present on real (non-demo) sessions. Empty when nothing new was earned.
  newStickers?: EarnedSticker[];
};
